import { useEffect, useRef } from 'react';
import { isDocumentMime, modelDisplayLabel, type Asset } from '@mg/shared';
import { Download } from 'lucide-react';
import { useProxyImage } from '../../lib/useProxyImage';
import { captureVideoPosterFromUrl } from '../../lib/videoPoster';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/display';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { useDialogs } from '../../components/ui/DialogProvider';
import {
  assetName,
  downloadAsset,
  formatBytes,
  fileTypeLabel,
  sourceBadge,
} from './mediaDisplay';

/**
 * Lightbox for a single asset. The blob is fetched through the auth image-proxy
 * (works for image/video/audio) and rendered with the right native control; the
 * prompt, tags, and meta are shown alongside. `asset === null` keeps it closed.
 */
export function AssetViewerModal({ asset, onClose }: { asset: Asset | null; onClose: () => void }) {
  const { alert } = useDialogs();
  // A PDF cannot be drawn here, and fetching all of it (up to 100 MB) only to offer a link was all
  // this did: show the first page drawn at upload instead, and fetch the file only on Download.
  const isDocument = isDocumentMime(asset?.mimeType);
  // Hook must run every render — fetch only while an asset is open.
  const objectUrl = useProxyImage(asset ? (isDocument ? (asset.thumbUrl ?? null) : asset.blobUrl) : null);
  const badge = asset ? sourceBadge(asset) : null;
  const modelLabel = asset ? modelDisplayLabel(asset.generator, asset.model) : null;

  /**
   * Give an older video a thumbnail, for free.
   *
   * Videos uploaded before posters existed have none, and nothing on the server can decode one — but
   * this modal has already downloaded the entire video in order to play it, and that object URL is
   * same-origin, so a frame is there for the taking at zero extra cost. Grabbing one here means every
   * video anyone actually opens stops being a film icon in the grid.
   *
   * Fire-and-forget, and once per asset: the ref stops a re-render from re-uploading, and the server
   * only fills a blank, so two tabs racing cannot clobber each other.
   */
  const posterFilled = useRef<string | null>(null);
  useEffect(() => {
    if (!asset || !objectUrl) return;
    if (asset.mediaType !== 'video' || asset.posterUrl) return;
    if (posterFilled.current === asset.id) return;
    posterFilled.current = asset.id;
    void (async () => {
      try {
        const poster = await captureVideoPosterFromUrl(objectUrl);
        if (poster) await api.setAssetPoster(asset.id, poster);
      } catch {
        // Opportunistic — the tile keeps its icon and nothing else changes.
      }
    })();
  }, [asset, objectUrl]);

  const doDownload = () => {
    if (!asset) return;
    void downloadAsset(asset).catch(() =>
      void alert({
        title: 'Something went wrong',
        message: 'Could not download this file — please try again.',
      }),
    );
  };

  return (
    <Modal
      open={asset != null}
      onClose={onClose}
      title={asset ? assetName(asset) : 'Media'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={doDownload}>
            <Download className="h-4 w-4" aria-hidden />
            Download
          </Button>
        </>
      }
    >
      {asset && (
        <div className="space-y-4">
          <div className="flex min-h-[12rem] items-center justify-center overflow-hidden rounded-lg border border-line bg-ink/5">
            {isDocument && !asset.thumbUrl ? (
              <p className="px-4 py-10 text-center text-sm text-ink-muted">
                No preview of this PDF yet. Use Download to open it.
              </p>
            ) : isDocument && objectUrl ? (
              <img
                src={objectUrl}
                alt={`First page of ${assetName(asset)}`}
                className="max-h-[50vh] w-auto bg-white object-contain"
              />
            ) : !objectUrl ? (
              <div className="flex items-center gap-2 py-10 text-sm text-ink-muted">
                <Spinner className="h-5 w-5 text-action" />
                Loading preview…
              </div>
            ) : asset.mediaType === 'image' ? (
              <img
                src={objectUrl}
                alt={assetName(asset)}
                // White behind the picture itself, so a transparent logo stays legible in the
                // dark theme; an opaque picture covers it completely.
                className="max-h-[50vh] w-auto bg-white object-contain"
              />
            ) : asset.mediaType === 'video' ? (
              <video src={objectUrl} controls className="max-h-[50vh] w-full" />
            ) : asset.mediaType === 'audio' ? (
              <audio src={objectUrl} controls className="w-full px-4 py-6" />
            ) : (
              <a
                href={objectUrl}
                download
                className="focus-ring py-10 text-sm font-medium text-link hover:text-action-hover hover:underline"
              >
                Download file
              </a>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
            {badge && <Badge tone={badge.tone}>{badge.label}</Badge>}
            <span>{fileTypeLabel(asset)}</span>
            <span aria-hidden>·</span>
            <span>{formatBytes(asset.sizeBytes)}</span>
            {asset.pageCount != null && (
              <>
                <span aria-hidden>·</span>
                <span>
                  {asset.pageCount} page{asset.pageCount === 1 ? '' : 's'}
                </span>
              </>
            )}
            <span aria-hidden>·</span>
            <span>Added {formatRelative(asset.createdAt)}</span>
          </div>

          {asset.campaigns.length > 0 && (
            <p className="text-sm text-ink-label">
              Used in{' '}
              <span className="font-medium text-ink">
                {asset.campaigns.map((c) => c.name).join(', ')}
              </span>
            </p>
          )}

          {asset.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {asset.tags.map((tag) => (
                <Badge key={tag} tone="neutral">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {asset.source === 'generation' && asset.prompt?.trim() && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Prompt
              </p>
              <p className="whitespace-pre-wrap rounded-lg border border-line bg-page/50 px-3 py-2 text-sm text-ink-strong">
                {asset.prompt}
              </p>
            </div>
          )}

          {/*
            Gated on the LABEL, not on `source` — `source` calls a Canva/Figma import a
            'generation', so gating on it would print an empty Model heading over every imported
            design. A null label already covers uploads, imports, and pre-column rows.
          */}
          {modelLabel && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Model
              </p>
              <p className="text-sm text-ink-strong">{modelLabel}</p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
